import React from "react";
import { Phone, Mail, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { STATUS_OPTIONS, STATUS_STYLES, fmtDate } from "../../lib/quoteStatus";

export const QuoteDetailDialog = ({
  detail,
  loading,
  onClose,
  onStatus,
  onDelete,
}) => (
  <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl bg-white border-2 border-navy rounded-none p-0 gap-0">
      <DialogHeader className="bg-navy text-white px-6 py-4 border-b-2 border-gold">
        <DialogTitle className="font-display uppercase text-xl tracking-wide">
          Quote Details
        </DialogTitle>
        <DialogDescription className="sr-only">
          Full quote submission details including contact info, service,
          description, and photo.
        </DialogDescription>
      </DialogHeader>
      {loading || !detail || !detail.name ? (
        <div className="p-12 flex items-center justify-center text-navy/60">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <p className="font-display uppercase text-2xl">{detail.name}</p>
            <span
              className={`font-display uppercase text-[10px] tracking-[0.15em] px-2 py-1 border-2 border-navy ${
                STATUS_STYLES[detail.status || "new"]
              }`}
            >
              {detail.status || "new"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Info label="Phone">
              <a
                href={`tel:${detail.phone}`}
                className="text-navy hover:text-gold inline-flex items-center gap-1"
              >
                <Phone className="h-3.5 w-3.5" /> {detail.phone}
              </a>
            </Info>
            <Info label="Email">
              <a
                href={`mailto:${detail.email}`}
                className="text-navy hover:text-gold inline-flex items-center gap-1"
              >
                <Mail className="h-3.5 w-3.5" /> {detail.email}
              </a>
            </Info>
            <Info label="Service">{detail.service}</Info>
            <Info label="Submitted">{fmtDate(detail.created_at)}</Info>
            {(detail.preferred_date || detail.preferred_window) && (
              <div
                className="sm:col-span-2 border-2 border-gold bg-gold/10 px-4 py-3"
                data-testid="booking-preference"
              >
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-navy/70 mb-1">
                  Preferred Appointment
                </p>
                <p className="font-display uppercase text-navy text-sm tracking-wide">
                  {detail.preferred_date || "Any date"}
                  {" · "}
                  {detail.preferred_window || "Any time"}
                </p>
              </div>
            )}
          </div>
          <Info label="Description">
            <p className="whitespace-pre-wrap text-sm">
              {detail.description || "—"}
            </p>
          </Info>
          {detail.photo_data_url && (
            <Info label="Photo">
              <img
                src={detail.photo_data_url}
                alt={detail.photo_filename || "Attachment"}
                className="w-full border-2 border-navy max-h-[400px] object-contain bg-offwhite"
              />
            </Info>
          )}

          <ConsentProof detail={detail} />

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-navy/15">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => onStatus(detail.id, s)}
                className={`px-3 py-2 font-display uppercase text-[10px] tracking-[0.15em] border-2 border-navy ${
                  detail.status === s
                    ? STATUS_STYLES[s]
                    : "bg-white text-navy hover:bg-navy/5"
                }`}
              >
                {s}
              </button>
            ))}
            <button
              onClick={() => onDelete(detail.id)}
              className="ml-auto inline-flex items-center gap-2 text-red-600 hover:text-red-700 px-3 py-2 font-display uppercase text-[10px] tracking-[0.15em] border-2 border-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const ConsentProof = ({ detail }) => (
  <div
    data-testid="consent-proof"
    className={`border-2 ${
      detail.sms_consent
        ? "border-emerald-600 bg-emerald-50"
        : "border-red-500 bg-red-50"
    } p-4`}
  >
    <p className="font-display uppercase text-[10px] tracking-[0.2em] text-navy/60 mb-2">
      SMS Consent (TCPA)
    </p>
    <p
      className={`font-display uppercase text-lg ${
        detail.sms_consent ? "text-emerald-700" : "text-red-700"
      }`}
    >
      {detail.sms_consent ? "✓ Consent given" : "✗ No consent — do not text"}
    </p>
    {detail.sms_consent && (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-navy/80">
        <ProofItem label="Timestamp:">
          {detail.consent_timestamp ? fmtDate(detail.consent_timestamp) : "—"}
        </ProofItem>
        <ProofItem label="Phone consented:">{detail.phone}</ProofItem>
        <ProofItem label="IP:">{detail.consent_ip || "—"}</ProofItem>
        <ProofItem label="Checkbox:">CHECKED</ProofItem>
        {detail.consent_user_agent && (
          <div className="sm:col-span-2 truncate">
            <span className="font-bold uppercase text-[10px] tracking-wider text-navy/60">
              Browser:
            </span>{" "}
            <span className="font-mono text-[10px]">
              {detail.consent_user_agent}
            </span>
          </div>
        )}
      </div>
    )}
    {detail.consent_text && (
      <p className="mt-3 text-[11px] leading-relaxed text-navy/70 border-t border-navy/15 pt-3 italic">
        "{detail.consent_text}"
      </p>
    )}
    {detail.consent_screenshot_data_url && (
      <div className="mt-3">
        <p className="font-bold uppercase text-[10px] tracking-wider text-navy/60 mb-2">
          Form Snapshot at Submission:
        </p>
        <a
          href={detail.consent_screenshot_data_url}
          download={`consent-${detail.id}.png`}
          className="block border-2 border-navy hover:opacity-90"
          title="Click to download full screenshot"
        >
          <img
            src={detail.consent_screenshot_data_url}
            alt="Form snapshot at submission"
            className="w-full max-h-[300px] object-contain bg-white"
          />
        </a>
        <p className="mt-1 text-[10px] text-navy/50 text-center">
          Click image to download full-resolution PNG
        </p>
      </div>
    )}
  </div>
);

const ProofItem = ({ label, children }) => (
  <div>
    <span className="font-bold uppercase text-[10px] tracking-wider text-navy/60">
      {label}
    </span>{" "}
    {children}
  </div>
);

const Info = ({ label, children }) => (
  <div>
    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1">
      {label}
    </p>
    <div className="text-navy">{children}</div>
  </div>
);
